// components/templates/sections/WhyChooseUsSection.tsx
'use client';
import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { useDesign } from '@/context/DesignContext';

interface TabContent {
  title: string;
  description: string;
  features: string[];
}

interface WhyChooseUsSectionProps {
  subtitle: string;
  title: string;
  tabs: TabContent[];
  images: string[];
  design?: any;
}

export default function WhyChooseUsSection({
  subtitle,
  title,
  tabs,
  images,
  design: propDesign,
}: WhyChooseUsSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  const tabsArray = Array.isArray(tabs) ? tabs : [];
  const [activeTab, setActiveTab] = useState(0);

  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const primaryColor = design?.colors?.primary || '#E07A5F';
  const textColor = design?.colors?.text || '#1E1E1E';
  const borderColor = design?.colors?.border || '#E9ECEF';
  const btnStyle = design?.buttons?.outline || {
    class: 'tf-button style3',
    backgroundColor: 'transparent',
    borderColor: 'var(--primary)',
    textColor: 'var(--primary)',
    borderRadius: '30px',
    padding: '10px 25px',
  };
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  const activeTabStyle: React.CSSProperties = {
    color: primaryColor,
    borderBottom: `2px solid ${primaryColor}`,
    cursor: 'pointer',
    padding: '10px 20px',
    fontFamily: headingFont,
  };
  const inactiveTabStyle: React.CSSProperties = {
    color: textColor,
    borderBottom: `1px solid ${borderColor}`,
    cursor: 'pointer',
    padding: '10px 20px',
    fontFamily: headingFont,
  };
  const bulletStyle: React.CSSProperties = {
    color: primaryColor,
    marginRight: '0.5rem',
  };
  const readMoreStyle: React.CSSProperties = {
    backgroundColor: btnStyle.backgroundColor,
    border: btnStyle.borderColor ? `1px solid ${btnStyle.borderColor}` : 'none',
    color: btnStyle.textColor,
    padding: btnStyle.padding,
    borderRadius: btnStyle.borderRadius,
    display: 'inline-block',
    textDecoration: 'none',
    marginTop: '1rem',
  };

  if (tabsArray.length === 0) {
    return <div>Aucune information disponible.</div>;
  }

  return (
    <section className="choose">
      <div className="shape"></div>
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto' }}>
        <div className="row">
          <div className="col-xl-6 col-md-12">
            <div className="choose-content">
              <div className="block-text">
                <p className="subtitle" style={{ fontFamily: headingFont, color: primaryColor }} data-aos="fade-up">{subtitle}</p>
                <h3 className="title" style={{ fontFamily: headingFont, color: textColor }} data-aos="fade-up">{title}</h3>
                <div className="flat-tabs" data-aos="fade-up">
                  <ul className="menu-tab" style={{ display: 'flex', listStyle: 'none', borderBottom: `1px solid ${borderColor}` }}>
                    {tabsArray.map((tab, idx) => (
                      <li
                        key={idx}
                        className={idx === activeTab ? 'active' : ''}
                        onClick={() => setActiveTab(idx)}
                        style={idx === activeTab ? activeTabStyle : inactiveTabStyle}
                      >
                        <h5>{tab.title}</h5>
                      </li>
                    ))}
                  </ul>
                  <div className="content-tab">
                    <div className="content-inner">
                      <div className="container_inner">
                        <p style={{ fontFamily: bodyFont }}>{tabsArray[activeTab].description}</p>
                        <ul className="list" style={{ listStyle: 'none', padding: 0 }}>
                          {tabsArray[activeTab].features.map((feat, i) => (
                            <li key={i} style={{ marginBottom: '0.5rem' }}>
                              <span style={bulletStyle}>✓</span> {feat}
                            </li>
                          ))}
                        </ul>
                        <Link href="#" className={btnStyle.class} style={readMoreStyle}>read more</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6 col-md-12">
            <div className="choose-image" data-aos="fade-left">
              <Swiper
                modules={[Navigation]}
                navigation={{ nextEl: '.swiper-button-next' }}
                className="swiperchoose"
              >
                {images.map((src, idx) => (
                  <SwiperSlide key={idx}>
                    <Image src={src} alt="" width={600} height={400} />
                  </SwiperSlide>
                ))}
                <div className="swiper-button-next">next <br /> image</div>
              </Swiper>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}